import {
  ArrowRightIcon,
  ClockIcon,
  EyeSlashIcon,
  ShoppingCartIcon,
} from '@heroicons/react/20/solid';
import { MegaphoneIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon, InboxIcon, PauseCircleIcon } from '@heroicons/react/24/outline';
import React, {
  type ClipboardEventHandler,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { InView } from 'react-intersection-observer';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LoadingIcon } from '@/components/UI/LoadingIcon';
import SoundPlayer from '@/components/UI/SoundPlayer';
import Header from '@/renderer/Header';
import { request } from '@/renderer/utils/api';
import { useMediaQuery } from '@/renderer/utils/hooks/useMediaQuery';
import { useLocale } from '@/renderer/utils/locale';
import { useIdleContext } from '@/renderer/utils/useIdle';
// import { useIdleTimerContext } from 'react-idle-timer';
import useMe from '@/renderer/utils/useMe';
import { useMemberships } from '@/renderer/utils/useMemberships';
import useRequest from '@/renderer/utils/useRequest';
import { PollingDelaysDefault, useInfiniteRequest } from '@/renderer/utils/useRequest2';
import type { RequestSchema } from '@/types/request';

import LiveNowIndicator from './LiveNowIndicator';
import Message from './Message';
import Release from './Release';
import SkeletonMessages from './SkeletonMessages';

const messageCreateRequest: RequestSchema<
  API.DirectMessageReceipt,
  API.WithData<{ content: string; receiverUserId: API.Id | '0'; type: 'dm' | 'dmh' }>
> = {
  name: 'messageCreateRequest',
  input: '/api/v1/message/create',
  method: 'POST',
};

const STATE_ACK = 4; // received
const STATE_OPENED = 5; // sent desktop notification
const STATE_READ = 6; // was shown alert
const STATE_VISITED = 7; // was shown message on Release Center

// Use a random timer 250-1750ms to avoid hammering the server all at once
const FLAG_TIMEOUT = Math.floor(250 + Math.random() * 1500);

const MessageReceiptLogger = React.memo(
  function MessageReceiptInner(messageReceipt: API.MessageReceipt) {
    const { id, web } = messageReceipt;
    const webStatus = useRef(web);

    useEffect(() => {
      webStatus.current = web;
    }, [web]);

    const idle = useIdleContext();
    const isIdle = useRef(idle.inactive);
    const idleLastActive = useRef(idle.lastActive);

    useEffect(() => {
      isIdle.current = idle.inactive;
    }, [idle.inactive]);

    useEffect(() => {
      idleLastActive.current = idle.lastActive;
    }, [idle.lastActive]);

    const messageReceiptFlagRequest: RequestSchema<
      API.DirectMessageReceipt,
      API.WithData<{ type: 'message' | 'dm'; web: number }>
    > = useMemo(
      () => ({
        name: 'messageReceiptFlagRequest',
        input: `/api/v1/messageReceipt/me/${id}/flag`,
        method: 'POST',
      }),
      [id],
    );

    const updateMessageRequest = useRequest(messageReceiptFlagRequest, { manual: true });

    useEffect(() => {
      let newState = webStatus.current;
      let timer: ReturnType<typeof setTimeout> | null | undefined;
      let fn: null | undefined | (() => void);
      let nextTimeout = 15000 + Math.random() * 5000;
      let isFirstRun = true;
      const startTime = idleLastActive.current;

      if (newState < STATE_VISITED) {
        // mark it as acknowledged if idle, visited if active
        fn = () => {
          timer = null;
          const wasRecentlyActive = isFirstRun
            ? !isIdle.current // on first pass, we only care if we are active right now
            : idleLastActive.current > startTime; // 2nd pass, check if we were active since the last check

          isFirstRun = false;

          if (
            newState <= STATE_ACK && // not yet read
            !wasRecentlyActive
          ) {
            if (newState < STATE_ACK) {
              updateMessageRequest.run({ data: { type: 'message', web: STATE_ACK } });
              webStatus.current = newState = STATE_ACK;
            }
          } else if (newState < STATE_VISITED) {
            // not yet read
            if (wasRecentlyActive) {
              fn = null;
              updateMessageRequest.run({ data: { type: 'message', web: STATE_VISITED } });
              webStatus.current = newState = STATE_VISITED;

              return; // return to avoid re-running read timer
            }
          }

          // Keep running the timer until they come back, so we can later flag it as VISITED
          if (fn) {
            timer = setTimeout(fn, nextTimeout); // second check is faster
            nextTimeout = 60000 + Math.random() * 30000; // third+ check is long
          }
        };

        timer = setTimeout(fn, FLAG_TIMEOUT);
      }

      if (timer && fn) {
        return () => {
          if (timer) {
            clearTimeout(timer);
            timer = null;

            if (fn) {
              fn(); // flag immediately on navigate away
              fn = null;
            }
          }
        };
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
  },
  (prev, next) => prev?.id === next?.id,
);

enum MediumFast {
  // Just started and have WS connected
  WS_CONNECTED_START = 1000,
  // Just found a message over non-WS and WS connected
  WS_CONNECTED_JUST_RECEIVED = 2000,
  // No data for a long time and WS connected
  WS_CONNECTED_MAX = 30000,
  // Out-of-business hours
  WS_CONNECTED_MAX_OOB = 200000,
  // Increase after each check by this amount
  WS_CONNECTED_INCREMENT = 1001,
  START = 999,
  JUST_RECEIVED = 2500,
  MAX = 11000,
  // Out-of-business hours
  MAX_OOB = 60000,
  INCREMENT = 500,
  ERROR_INCREMENT = 5000,
}

enum VeryFast {
  // Just started and have WS connected
  WS_CONNECTED_START = 1000,
  // Just found a message over non-WS and WS connected
  WS_CONNECTED_JUST_RECEIVED = 1001,
  // No data for a long time and WS connected
  WS_CONNECTED_MAX = 20000,
  // Out-of-business hours
  WS_CONNECTED_MAX_OOB = 200000,
  // Increase after each check by this amount
  WS_CONNECTED_INCREMENT = 300,
  START = 999,
  JUST_RECEIVED = 2500,
  MAX = 2501,
  // Out-of-business hours
  MAX_OOB = 30000,
  INCREMENT = 200,
  ERROR_INCREMENT = 5000,
}

export default function MessageReceiptList() {
  const currentUser = useMe();
  const memberships = useMemberships({ mergeSame: true, omitEmpty: false });
  const intl = useLocale();
  const scrollContainer = useRef<HTMLElement>(null);
  const loadOlderRef = useRef<HTMLButtonElement>(null);

  const userRef = useRef(currentUser);

  if (userRef.current !== currentUser) {
    userRef.current = currentUser;
  }

  const eventValidate = useCallback(
    async (
      event: MessageEvent,
      ojson: API.SubscriptionResponse<API.MessageReceipt> | API.WebsocketResult<API.Id>,
    ) => {
      if (ojson.message === 'messageReceipt/updated') {
        return {
          ...ojson,
          data: (
            await request<API.WithData<API.MessageReceipt>>(
              `/api/v1/messageReceipt/me/${ojson.data as number}`,
              userRef.current?.role === 'admin' || userRef.current?.role === 'staff'
                ? { query: { withMessageMeta: true } }
                : undefined,
            )
          ).data,
        } satisfies API.SubscriptionResponse<API.MessageReceipt>;
      }
    },
    [],
  );

  const sendMessageRequest = useRequest(messageCreateRequest, { manual: true });

  const onCopy: ClipboardEventHandler<HTMLParagraphElement> = useCallback(async () => {
    try {
      const selectedText = document.getSelection()?.toString().trim();

      if (selectedText) {
        await sendMessageRequest.run({
          data: {
            content: selectedText,
            receiverUserId: '0',
            type: 'dmh',
          },
        });
      }
    } catch (error) {
      if (import.meta.env.PROD) {
        console.error(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [PollingDelays, setPollingDelays] = useState<
    typeof MediumFast | typeof VeryFast | undefined
  >(undefined);

  const {
    isPending,
    isSuccess,
    error,
    data: results,
    isFetching,
    isFetchingNextPage,
    // isFetchingPreviousPage,
    fetchNextPage: loadOlder,
    // fetchPreviousPage,
    hasNextPage,
    // hasPreviousPage,
    wsConnected,
    newestItem,
    oldestItem,
    renderPayload,
  } = useInfiniteRequest<API.MessageReceipt, void, API.WithPageInput<{ withMessageMeta: boolean }>>(
    {
      key: 'fetchMessages',
      req: `/api/v1/messageReceipt/me`,
      query:
        userRef.current?.role === 'admin' || userRef.current?.role === 'staff'
          ? { withMessageMeta: true }
          : undefined,
      event: 'messageReceipt',
      eventValidate,
      PollingDelays: PollingDelays || PollingDelaysDefault,
    },
  );
  const updateSpeed = useMemo(() => {
    if (!results?.data || !results?.data.length) {
      return undefined;
    }

    const platform = `${
      typeof navigator !== 'undefined' && (navigator.userAgentData?.platform || navigator.platform)
    }`;
    const isIosOrAndroid = platform.startsWith('i') || platform.toLowerCase().includes('android');

    if (!isIosOrAndroid) {
      return undefined;
    }

    const now = Date.now();

    // Check the last 50 messages
    for (let i = 0; i < 50; i++) {
      const receipt = results.data[i];

      if (!receipt) {
        break;
      }

      const createdAt = +new Date(receipt.createdAt);
      const { content, release, notify } = receipt.message;

      if (now - createdAt > 60 * 60 * 4 * 1000) {
        // too old
        break;
      }

      if (notify !== 'releaseWarning' && !release) {
        return MediumFast;
      }

      if (/^\W*(Done|No more releases)\W*([.!]*|for )/i.test(content)) {
        return undefined;
      } else if (
        notify === 'releaseWarning' ||
        /^\W*(We will start in|We will get started in|Will get started in|Will get started shortly|Next prop in [0-9]+ (?:seconds|minutes)|Here we go...|)\W*([.!]*|for )/i.test(
          content,
        )
      ) {
        return VeryFast;
      } else if (release || /^\W*GO\W*[.!]*$/i.test(content)) {
        return VeryFast;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, results?.data.length, results?.data?.[0]]);

  useEffect(() => {
    setPollingDelays(updateSpeed);
  }, [updateSpeed]);

  const lowCreditsMemberships: API.Membership<true>[] = [];
  const emptyCreditsMemberships: API.Membership<true>[] = [];
  let noMemberships = false;

  if (!memberships.isPending && memberships.data?.data) {
    noMemberships = true;

    for (const membership of memberships.data.data) {
      const warningCount =
        typeof membership.product?.stats?.buyable === 'number'
          ? Math.min(10, Math.max(membership.remaining, membership.product.stats.buyable))
          : 5;

      if (
        warningCount >= 1 &&
        membership.remaining < warningCount &&
        !membership.bonusId &&
        membership.productId !== 44
      ) {
        if (membership.remaining === 0) {
          emptyCreditsMemberships.push(membership);
        } else {
          lowCreditsMemberships.push(membership);
        }
      }

      if (membership.remaining > 0) {
        noMemberships = false;
      }
    }
  }

  const isEmpty = !newestItem && !oldestItem;

  const doScroll = useCallback((selector: string, top?: boolean) => {
    const el = scrollContainer.current?.querySelector(selector);

    if (top) {
      scrollContainer.current?.scrollTo({ top: 1 });
    }

    el?.scrollIntoView?.({ block: 'start' });

    setTimeout(() => {
      const el2 = scrollContainer.current?.querySelector(selector);

      if (top) {
        scrollContainer.current?.scrollTo({ top: -1 });
      }

      el2?.scrollIntoView?.({ block: 'start' });
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previousOldestId = useRef(oldestItem?.id);
  const previousNewestId = useRef(newestItem?.id);

  useEffect(() => {
    if (!scrollContainer.current || !results?.data.length) {
      return;
    }

    const scrollTop = Math.abs(scrollContainer.current.scrollTop);

    if (newestItem?.id && newestItem.id !== previousNewestId.current) {
      // New entry, scroll to end
      previousNewestId.current = newestItem.id;

      doScroll(`[data-receiptid="${parseInt(`${newestItem.id}`, 10)}"]`, true);
    } else if (oldestItem?.id && oldestItem.id !== previousOldestId.current) {
      // Loaded more at the end, scroll to beginning if near the top
      previousOldestId.current = oldestItem.id;
      const scrollDistance =
        scrollContainer.current.scrollHeight - scrollContainer.current.clientHeight - scrollTop;

      if (scrollDistance < 250 && oldestItem?.id) {
        doScroll(`[data-receiptid="${parseInt(`${oldestItem.id}`, 10)}"]`);
      }
    } else {
      // Possibly an update, scroll to end if already scrolled almost to end
      if (scrollTop < 250 && scrollTop > 0 && newestItem?.id) {
        doScroll(`[data-receiptid="${parseInt(`${newestItem.id}`, 10)}"]`, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oldestItem?.id, newestItem?.id, results]);

  let metaMessage = '';

  if (currentUser?.meta?.emailUnverified) {
    metaMessage = 'Your email address is currently unverified.';
  } else if (currentUser?.meta?.email) {
    metaMessage =
      'Your email address change has not yet been confirmed. Emails will continue to go to your old address.';
  } else if (
    (currentUser?.meta?.contactSecondary && !currentUser.contactSecondary) ||
    (currentUser?.meta?.contactTertiary && !currentUser.contactTertiary)
  ) {
    metaMessage = 'Your additional contact methods have not yet been confirmed for use.';
  } else if (currentUser?.meta?.contactSecondary || currentUser?.meta?.contactTertiary) {
    metaMessage = 'Your additional contact methods change has not yet been confirmed.';
  }

  const smsStatus2 = currentUser?.meta?.contactSecondaryStatus;
  const smsStatus3 = currentUser?.meta?.contactTertiaryStatus;
  const metaStatusValue =
    smsStatus2?.state && smsStatus2?.state !== 'DELIVERED' && smsStatus2?.state !== 'PENDING'
      ? smsStatus2
      : smsStatus3?.state && smsStatus3?.state !== 'DELIVERED' && smsStatus3?.state !== 'PENDING'
        ? smsStatus3
        : null;

  const isDesktop = useMediaQuery('(min-width: 640px)');

  return (
    <>
      <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
        <Header title="Release Center" Icon={MegaphoneIcon} className="py-2">
          <div className="ml-2 mr-auto self-center">
            <SoundPlayer
              text={
                isDesktop
                  ? "You're not receiving warning sounds. Click anywhere to enable."
                  : 'No warning sounds. Click to enable.'
              }
              small
            />
          </div>
          <LiveNowIndicator receipts={results?.data} />
        </Header>

        {error && (
          <aside className="bg-amber-50 p-4">
            <div className="flex flex-row items-center space-x-2 text-sm text-amber-700">
              <ExclamationCircleIcon className="size-6 shrink-0" aria-hidden="true" />
              <span>Error: {error.message}</span>
            </div>
          </aside>
        )}

        {!!currentUser?.paused && (
          <aside className="bg-blue-50 p-4">
            <div className="flex flex-row items-center space-x-2 text-sm text-blue-700">
              <PauseCircleIcon className="size-6 shrink-0" aria-hidden="true" />
              <span>
                You have paused your account.{' '}
                <a
                  href="/settings/memberships"
                  className="text-blue-700 underline hover:text-blue-600"
                >
                  Unpause your account to receive new releases.
                </a>
              </span>
            </div>
          </aside>
        )}

        {!!metaMessage && (
          <aside className="bg-blue-50 p-4">
            <div className="flex flex-row items-center space-x-2 text-sm text-blue-700">
              <PauseCircleIcon className="size-6 shrink-0" aria-hidden="true" />
              <span>
                {metaMessage}{' '}
                <a href="/settings" className="text-blue-700 underline hover:text-blue-600">
                  Visit your account settings to change.
                </a>
              </span>
            </div>
          </aside>
        )}

        {!!metaStatusValue?.state &&
          metaStatusValue.state !== 'PENDING' &&
          metaStatusValue.state !== 'DELIVERED' && (
            <aside className="bg-amber-50 p-4">
              <div className="flex flex-row items-end space-x-2 text-sm text-amber-700">
                <span>
                  We recently encountered a problem sending to {metaStatusValue.to}. In some cases,
                  you can resolve this problem by texting <code>START</code> or <code>UNSTOP</code>{' '}
                  to{' '}
                  <a
                    href="sms://+18667277435"
                    className="whitespace-nowrap text-nowrap text-amber-800 underline"
                  >
                    +1 866 727 7435
                  </a>
                  . Error Code: <code>{metaStatusValue.state}</code>.{' '}
                  <a href="/settings" className="text-amber-700 underline hover:text-amber-600">
                    Visit your account settings to manage.
                  </a>
                </span>
              </div>
            </aside>
          )}

        {/* {noMemberships && (
          <aside className="bg-amber-50 p-4">
            <div className="flex flex-row items-center space-x-2 text-sm text-amber-700">
              <ExclamationCircleIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
              <span>
                You don't have any active services
                {currentUser?.tags?.includes('free') ? ', apart from free plays' : ''}.{' '}
                <a href="/order" className="text-amber-700 underline hover:text-amber-600">
                  Order now
                </a>{' '}
                to avoid missing any plays!
              </span>
            </div>
          </aside>
        )} */}

        {!!lowCreditsMemberships.length && (
          <aside className="bg-amber-50 p-4">
            <div className="flex flex-row items-center space-x-2 text-sm text-amber-700">
              <ExclamationCircleIcon className="size-6 shrink-0" aria-hidden="true" />
              <span>
                You&apos;re almost out of play credits on{' '}
                {lowCreditsMemberships.length === 1 ? 'one' : 'some'} of your services.{' '}
                <a href="/order" className="text-amber-700 underline hover:text-amber-600">
                  Add more
                </a>{' '}
                to avoid missing any plays!
              </span>
            </div>
          </aside>
        )}

        {!!emptyCreditsMemberships.length && (
          <aside className="bg-amber-50 p-4">
            <div className="flex flex-row items-center space-x-2 text-sm text-red-700">
              <ExclamationCircleIcon className="size-6 shrink-0" aria-hidden="true" />
              <span>
                You&apos;ve run out of play credits on{' '}
                {emptyCreditsMemberships.length === 1 ? 'one' : 'some'} of your services.{' '}
                <a href="/order" className="text-amber-700 underline hover:text-red-600">
                  Add more
                </a>{' '}
                to avoid missing any plays!
              </span>
            </div>
          </aside>
        )}

        {!!error && !wsConnected && (
          <aside className="bg-red-50 p-4">
            <div className="flex flex-row items-center space-x-2 text-sm text-red-700">
              <ExclamationCircleIcon className="size-6 shrink-0" aria-hidden="true" />
              <span>There seems to be a problem loading messages. We'll keep trying...</span>
            </div>
          </aside>
        )}
        {/* {!isPending && !wsConnected && (
          <aside className="bg-yellow-50 p-4">
            <div className="flex flex-row items-center space-x-2 text-sm text-yellow-700">
              <SignalSlashIcon className="h-6 w-6" />
              <span>Degraded connection. Messaging service may be delayed.</span>
            </div>
          </aside>
        )} */}
        {isPending && <SkeletonMessages />}
        {isSuccess && isEmpty && !error && (
          <aside className="mx-auto flex h-auto items-center justify-center overflow-y-scroll text-center dark:border-zinc-700">
            <div className="my-auto flex min-h-fit flex-col items-center justify-center space-y-8 rounded-lg py-4 text-center">
              <div className="space-y-2">
                <h2 className="text-2xl font-normal text-zinc-900 dark:text-white">
                  Welcome to RAS
                </h2>
                <p className="max-w-prose text-sm leading-6 text-zinc-500 dark:text-zinc-300">
                  Thanks for joining us,{' '}
                  <span className="font-bold text-zinc-800 dark:text-zinc-200">
                    {currentUser?.firstName}
                  </span>
                  !
                </p>
              </div>
              <div className="space-y-8">
                <div className="mx-2 flex w-fit flex-col space-y-8 rounded border-2 border-dashed border-zinc-500 p-12 sm:mx-auto">
                  <InboxIcon className="mx-auto size-12 stroke-1 text-center text-zinc-500 dark:text-zinc-400" />
                  <h2 className="text-lg text-zinc-800 dark:text-white">
                    You haven't received any releases yet
                  </h2>
                  <p className="max-w-md text-sm leading-6 text-zinc-500 dark:text-zinc-300">
                    This is where all plays will be released. If you've recently subscribed to a
                    service, wait for a release warning and return here.
                  </p>
                  <div className="bg-zinc-900/5 p-4 text-zinc-600 shadow-sm dark:bg-white/5 dark:text-zinc-400 dark:shadow-inner dark:shadow-white/5">
                    <ul className="mx-auto w-fit space-y-4 pl-0 text-left text-sm">
                      <li className="flex flex-row items-start justify-start space-x-2 sm:items-center">
                        <ClockIcon className="size-5 shrink-0" />
                        <span>Only new releases will be delivered here.</span>
                      </li>
                      <li className="flex flex-row items-start justify-start space-x-2 sm:items-center">
                        <EyeSlashIcon className="size-5 shrink-0" />
                        <span>Previously released plays will not be shown here.</span>
                      </li>
                      <li className="flex flex-row items-start justify-start space-x-2 sm:items-center">
                        <ShoppingCartIcon className="size-5 shrink-0" />
                        <span>You need to order a service to receive paid releases.</span>
                      </li>
                    </ul>
                  </div>
                  {/* <div className="mt-4 flex items-center gap-x-3 sm:mx-auto">
                  <button className="w-1/2 rounded-lg border bg-white px-5 py-2 text-sm text-gray-700 transition-colors duration-200 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 sm:w-auto">
                    Customer Support
                  </button>
                  <button className="flex w-1/2 shrink-0 items-center justify-center gap-x-2 rounded-lg bg-blue-500 px-5 py-2 text-sm tracking-wide text-white transition-colors duration-200 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500 sm:w-auto">
                    <span>Buy a service</span>
                  </button>
                </div> */}
                </div>
                {/* <h2 className="text-base text-zinc-800 dark:text-white">
                  Make sure your account is ready
                </h2>
                <p className="max-w-prose text-sm leading-6 text-zinc-500 dark:text-zinc-400">
                  Release warnings will be sent to all email addresses and phone numbers listed in
                  your account settings.
                </p>
                <a href="/settings" className="flex items-center justify-center space-x-1 text-sm">
                  <span>Check account settings</span>
                  <ArrowRightIcon className="h-4 w-4" />
                </a> */}
                <div className="flex items-baseline justify-center space-x-2 text-sm text-zinc-800 dark:text-zinc-200">
                  <span>Still need to order a service?</span>
                  <a href="/order" className="flex items-center space-x-1 text-emerald-500">
                    <span>Order here</span>
                    <ArrowRightIcon className="size-4" />
                  </a>
                </div>
              </div>
            </div>
          </aside>
        )}

        {!isSuccess && !results && !isPending && (
          <>Sorry, something seems to be wrong with our servers at the moment.</>
        )}

        {!isEmpty && (
          <section
            className="flex h-fit grow flex-col-reverse overflow-y-auto"
            ref={scrollContainer}
          >
            <div className="flex flex-col pt-4" onCopy={onCopy}>
              {hasNextPage && (
                <aside className="text-center">
                  <InView
                    as="aside"
                    className="text-center"
                    onChange={(inView, _entry) => {
                      if (
                        inView &&
                        isSuccess &&
                        !isFetching &&
                        !isFetchingNextPage &&
                        loadOlderRef.current
                      ) {
                        scrollContainer.current
                          ?.querySelector('[data-receiptid]')
                          ?.scrollIntoView({ block: 'start' });

                        const timeout = setTimeout(() => loadOlderRef.current?.click?.(), 100);

                        return () => {
                          clearTimeout(timeout);
                        };
                      }
                    }}
                  ></InView>
                  <button
                    disabled={isFetchingNextPage}
                    data-loading={isFetchingNextPage}
                    ref={loadOlderRef}
                    onClick={() => loadOlder()}
                    className="m-0 cursor-pointer select-none border-0 bg-transparent p-2 text-xs text-zinc-600 hover:text-zinc-500 data-[loading=true]:cursor-progress dark:text-zinc-300 dark:hover:text-zinc-200"
                  >
                    Load older messages
                    <LoadingIcon isLoading={isFetchingNextPage} />
                  </button>
                </aside>
              )}
              {renderPayload((payload) => {
                const receipts = [...payload.data].reverse();

                return receipts.map((props, receiptIndex) => {
                  const receiptTime = new Date(props.createdAt);
                  const olderReceipt = receipts[receiptIndex - 1];

                  const date = intl.formatDate(props.createdAt, {
                    month: 'long',
                    day: 'numeric',
                    weekday: 'short',
                  });

                  let showDate = true;
                  let showUserInfo = true;

                  if (olderReceipt) {
                    const olderReceiptTime = new Date(olderReceipt.createdAt);
                    const olderDate = intl.formatDate(olderReceipt.createdAt, {
                      month: 'long',
                      day: 'numeric',
                      weekday: 'short',
                    });

                    if (olderDate === date) {
                      showDate = false;

                      if (+receiptTime - +olderReceiptTime <= 5 * 60 * 1000) {
                        showUserInfo = false;
                      }
                    }
                  }

                  const retjsx = (
                    <React.Fragment key={props.id}>
                      {showDate && (
                        <article className="my-4 flex items-center px-4">
                          <hr className="h-px grow border-0 bg-zinc-900/10 dark:bg-white/10" />
                          <time className="shrink-0 px-2 text-xs text-zinc-900/50 dark:text-zinc-500">
                            {date}
                          </time>
                          <hr className="h-px grow border-0 bg-zinc-900/10 dark:bg-white/10" />
                        </article>
                      )}

                      {props.release ? (
                        <Message receipt={props} userInfo={showUserInfo}>
                          <Release release={props.release} />
                        </Message>
                      ) : (
                        <Message receipt={props} userInfo={showUserInfo} />
                      )}

                      <ErrorBoundary>
                        <Suspense>
                          <MessageReceiptLogger {...props} />
                        </Suspense>
                      </ErrorBoundary>
                    </React.Fragment>
                  );

                  return retjsx;
                });
              })}
              <article className="bg-gradient-to-b from-transparent to-zinc-100 dark:to-black">
                <div className="flex items-center p-4">
                  <hr className="h-px grow border-0 bg-zinc-900/10 dark:bg-white/10" />
                  <MegaphoneIcon className="h-4 w-auto shrink-0 px-2 text-zinc-400 dark:text-zinc-600" />
                  <hr className="h-px grow border-0 bg-zinc-900/10 dark:bg-white/10" />
                </div>
              </article>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
